/**
 * Workshop Booking - events proxy fetch.
 *
 * joinProxyPath + fetchWorkshopEvents, extracted verbatim from
 * workshop-booking.js. Proxy base/path, timeout and dev-logging flag are injected
 * via create() so the module has no closure coupling to the booking root. The
 * hung-proxy TimeoutError guard and 404 legacy-path retry are unchanged.
 *
 * Loaded (defer) before workshop-booking.js. Exposes window.WorkshopBookingEvents.
 *
 * Usage (in workshop-booking.js):
 *   var ev = WorkshopBookingEvents.create({ proxyBase, proxyBaseFallback,
 *     eventsPath, timeoutMs, devMode });
 *   ev.fetchWorkshopEvents(qs, { method:'GET', signal, requestId });
 *   ev.joinProxyPath(base, path);   // also WorkshopBookingEvents.joinProxyPath
 */
(function (global) {
  if (global.WorkshopBookingEvents) return;

  /** Safe join: trim trailing "/" from base, ensure path starts with "/", prevent segment duplication. */
  function joinProxyPath(base, path) {
    var b = String(base).replace(/\/+$/, '');
    var p = String(path);
    if (p.charAt(0) !== '/') p = '/' + p;
    var last = b.split('/').pop();
    var first = p.split('/').filter(Boolean)[0];
    if (last && first && last === first) b = b.slice(0, b.length - last.length - 1);
    return b + p;
  }

  function create(cfg) {
    cfg = cfg || {};
    var PROXY_BASE = cfg.proxyBase || '/apps/workshop-events';
    var PROXY_BASE_FALLBACK = cfg.proxyBaseFallback || '/apps/workshop-event';
    var EVENTS_PATH = cfg.eventsPath || '/events-v';
    var EVENTS_FETCH_TIMEOUT_MS = cfg.timeoutMs != null ? cfg.timeoutMs : 15000;
    var DEV_MODE = !!cfg.devMode;

    /** Fetch events from proxy; on 404 retry once with legacy path. AbortController and requestId handled by caller. */
    function fetchWorkshopEvents(queryString, fetchOpts) {
      var qs = queryString;
      if (DEV_MODE && qs.indexOf('debug=') === -1) qs = qs + (qs ? '&' : '') + 'debug=1';
      var url = joinProxyPath(PROXY_BASE, EVENTS_PATH) + '?' + qs;
      var requestId = fetchOpts && fetchOpts.requestId;
      var startMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
      if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' url=' + url + ' start');
      var opts = Object.assign({}, fetchOpts);
      delete opts.requestId;
      var doFetch = fetch(url, opts).then(function (res) {
        if (res.status === 404) {
          if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' 404 fallback');
          console.warn('[Workshop] App proxy returned 404 for ' + url + '; retrying with legacy path');
          return fetch(joinProxyPath(PROXY_BASE_FALLBACK, EVENTS_PATH) + '?' + qs, opts);
        }
        var endMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
        if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' status=' + res.status + ' end ms=' + Math.round(endMs - startMs) + ' aborted=false');
        return res;
      });
      if (!(EVENTS_FETCH_TIMEOUT_MS > 0)) {
        return doFetch.catch(function (err) {
          if (DEV_MODE && requestId != null && err && err.name === 'AbortError') console.log('[WB_EVENTS] request_id=' + requestId + ' aborted=true');
          throw err;
        });
      }
      // Guard against a hung proxy leaving the calendar overlay spinning forever.
      // Callers already pass their own abort signal (for supersede) and silently
      // swallow AbortError, so aborting here would leave the overlay stuck. Instead
      // race against a timer that rejects with a distinct TimeoutError, which flows
      // into each caller's real error-UI branch (not the silent AbortError return).
      var timeoutId = 0;
      var timeoutP = new Promise(function (_resolve, reject) {
        timeoutId = setTimeout(function () {
          var e = new Error('events_timeout');
          e.name = 'TimeoutError';
          e.isTimeout = true;
          if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' timeout ms=' + EVENTS_FETCH_TIMEOUT_MS);
          reject(e);
        }, EVENTS_FETCH_TIMEOUT_MS);
      });
      return Promise.race([doFetch, timeoutP])
        .then(function (res) {
          if (timeoutId) { clearTimeout(timeoutId); timeoutId = 0; }
          return res;
        })
        .catch(function (err) {
          if (timeoutId) { clearTimeout(timeoutId); timeoutId = 0; }
          if (DEV_MODE && requestId != null && err && err.name === 'AbortError') console.log('[WB_EVENTS] request_id=' + requestId + ' aborted=true');
          throw err;
        });
    }

    return { joinProxyPath: joinProxyPath, fetchWorkshopEvents: fetchWorkshopEvents };
  }

  global.WorkshopBookingEvents = { create: create, joinProxyPath: joinProxyPath };
})(typeof window !== 'undefined' ? window : this);
